import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Loader2, Printer, RefreshCcw } from 'lucide-react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';

type LabelSize = '45x20' | '45x45';

type ReceivingStickerItem = {
  lineId: string;
  inventoryId?: string | null;
  inventoryCode?: string | null;
  skuId?: string | null;
  skuCode: string;
  description: string;
  qcCode?: string | null;
  projectCode?: string | null;
  poNumber?: string | null;
  qtyExpected: number;
  qtyReceived: number;
  lotNumber?: string | null;
  heatNo?: string | null;
  millCertNo?: string | null;
  countryOfOrigin?: string | null;
  documentUpload?: string | null;
  stockNo?: string | null;
  binCode?: string | null;
  status?: string | null;
  printQty: number;
};

type ReceivingStickerOrder = {
  id: string;
  asnNumber: string;
  sourceOrderNumber?: string | null;
  originName?: string | null;
  status?: string | null;
};

type StickerPrintDataResponse = {
  order: ReceivingStickerOrder;
  items: ReceivingStickerItem[];
};

type StickerLabelResponse = {
  labels: Array<{
    lineId: string;
    inventoryId?: string | null;
    inventoryCode?: string | null;
    skuCode: string;
    description: string;
    qcCode?: string | null;
    projectCode?: string | null;
    poNumber?: string | null;
    shareUrl: string;
    localShareUrl?: string;
    copies: number;
  }>;
};

function htmlEscape(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function defaultPrintQty(item: ReceivingStickerItem): number {
  const received = Number(item.qtyReceived ?? 0);
  const expected = Number(item.qtyExpected ?? 0);
  return Math.max(1, Math.round(received > 0 ? received : expected || item.printQty || 1));
}

function stickerCss(size: LabelSize): string {
  const height = size === '45x45' ? 45 : 20;
  const isSquare = size === '45x45';
  return `
    @page { margin: 0; size: 45mm ${height}mm; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #fff; color: #000; }
    .sheet { display: flex; flex-wrap: wrap; align-items: flex-start; }
    .label {
      width: 45mm;
      height: ${height}mm;
      display: grid;
      grid-template-rows: ${isSquare ? '6mm 31mm 8mm' : '3.5mm 12mm 4.5mm'};
      row-gap: ${isSquare ? '0.7mm' : '0.45mm'};
      padding: ${isSquare ? '2mm' : '1mm 1.4mm'};
      border: 1px solid #111;
      overflow: hidden;
      page-break-inside: avoid;
      break-inside: avoid;
      font-family: Arial, Helvetica, sans-serif;
    }
    .sku {
      min-width: 0;
      overflow: hidden;
      text-align: center;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
      font-size: ${isSquare ? '8pt' : '6.5pt'};
      font-weight: 800;
      line-height: 1;
    }
    .qr { min-height: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .qr img { display: block; width: auto; height: 100%; max-width: 100%; image-rendering: pixelated; }
    .desc {
      min-width: 0;
      overflow: hidden;
      padding-top: ${isSquare ? '0.25mm' : '0.15mm'};
      text-align: center;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      font-size: ${isSquare ? '6pt' : '4.8pt'};
      font-weight: 700;
      line-height: 1.05;
    }
  `;
}

function buildStickerPrintHtml(orderNumber: string, size: LabelSize, labels: Array<{ inventoryCode?: string | null; skuCode: string; description: string; qrDataUrl: string }>) {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receiving Stickers - ${htmlEscape(orderNumber)}</title>
  <style>${stickerCss(size)}</style>
</head>
<body>
  <div class="sheet">
    ${labels.map((label) => `
      <div class="label">
        <div class="sku">${htmlEscape(label.inventoryCode || label.skuCode)}</div>
        <div class="qr"><img src="${label.qrDataUrl}" alt="" /></div>
        <div class="desc">${htmlEscape(label.description)}</div>
      </div>
    `).join('')}
  </div>
</body>
</html>`;
}

export function WmsReceivingStickerPanel({
  inboundOrderId,
  onRequestPrintPreview,
}: {
  inboundOrderId: string;
  onRequestPrintPreview?: (payload: { title: string; html: string }) => void;
}) {
  const [data, setData] = useState<StickerPrintDataResponse | null>(null);
  const [qtyByLine, setQtyByLine] = useState<Record<string, number>>({});
  const [size, setSize] = useState<LabelSize>('45x20');
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [scanLoadingLineId, setScanLoadingLineId] = useState<string | null>(null);
  const [scanPreview, setScanPreview] = useState<{ open: boolean; title: string; url: string }>({
    open: false,
    title: 'Scan Preview',
    url: '',
  });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<StickerPrintDataResponse>(`/wms/inbound-orders/${encodeURIComponent(inboundOrderId)}/sticker-print-data`);
      setData(res);
      setQtyByLine(Object.fromEntries((res.items ?? []).map((item) => [item.lineId, defaultPrintQty(item)])));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load receiving sticker data');
    } finally {
      setLoading(false);
    }
  }, [inboundOrderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const printableItems = useMemo(() => {
    return (data?.items ?? [])
      .map((item) => ({ ...item, printQty: Math.max(0, Math.min(500, Number(qtyByLine[item.lineId] ?? 0))) }))
      .filter((item) => item.printQty > 0);
  }, [data?.items, qtyByLine]);

  const handlePrint = async () => {
    if (!data || printableItems.length === 0) return;
    setPrinting(true);
    setError('');
    try {
      const res = await api.post<StickerLabelResponse>(`/wms/inbound-orders/${encodeURIComponent(inboundOrderId)}/sticker-labels`, {
        labelSize: size,
        items: printableItems.map((item) => ({
          lineId: item.lineId,
          inventoryId: item.inventoryId ?? undefined,
          printQty: item.printQty,
        })),
      });

      const expandedLabels: Array<{ inventoryCode?: string | null; skuCode: string; description: string; shareUrl: string }> = [];
      for (const label of res.labels ?? []) {
        for (let i = 0; i < Math.max(1, Number(label.copies ?? 1)); i += 1) {
          expandedLabels.push(label);
        }
      }
      const labelsWithQr = await Promise.all(expandedLabels.map(async (label) => ({
        ...label,
        qrDataUrl: await QRCode.toDataURL(label.shareUrl, {
          margin: 0,
          errorCorrectionLevel: 'M',
          width: 520,
          color: { dark: '#000000', light: '#ffffff' },
        }),
      })));
      const html = buildStickerPrintHtml(data.order.asnNumber, size, labelsWithQr);
      if (onRequestPrintPreview) {
        onRequestPrintPreview({
          title: `Receiving Stickers - ${data.order.asnNumber}`,
          html,
        });
        return;
      }

      const popup = window.open('', '_blank', 'popup=yes,width=980,height=760');
      if (!popup) throw new Error('Popup blocked. Please allow popups to print stickers.');
      popup.document.open();
      popup.document.write(`${html}<script>window.onload = () => setTimeout(() => window.print(), 150);</script>`);
      popup.document.close();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to print stickers');
    } finally {
      setPrinting(false);
    }
  };

  const handleOpenScan = async (item: ReceivingStickerItem) => {
    setScanLoadingLineId(item.lineId);
    setError('');
    try {
      const res = await api.post<StickerLabelResponse>(`/wms/inbound-orders/${encodeURIComponent(inboundOrderId)}/sticker-labels`, {
        labelSize: size,
        items: [{
          lineId: item.lineId,
          inventoryId: item.inventoryId ?? undefined,
          printQty: 1,
        }],
      });
      const link = res.labels?.[0]?.localShareUrl || res.labels?.[0]?.shareUrl;
      if (!link) throw new Error('Scan link is not available');
      setScanPreview({
        open: true,
        title: item.inventoryCode || item.skuCode || 'Scan Preview',
        url: link,
      });
    } catch (err: any) {
      setError(err?.message ?? 'Failed to open scan link');
    } finally {
      setScanLoadingLineId(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading receiving stickers...
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border bg-muted/20 p-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Printer className="h-4 w-4" />
            Receiving Product Stickers
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {data?.order.asnNumber ?? inboundOrderId}
            {data?.order.sourceOrderNumber ? ` / ${data.order.sourceOrderNumber}` : ''}
            {data?.order.originName ? ` / ${data.order.originName}` : ''}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border bg-background text-xs">
            {([
              { value: '45x20' as const, label: '45mm x 20mm' },
              { value: '45x45' as const, label: '45mm x 45mm' },
            ]).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSize(option.value)}
                className={`px-3 py-1.5 font-medium ${size === option.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />}
            Refresh
          </Button>
          <Button size="sm" onClick={() => void handlePrint()} disabled={printing || printableItems.length === 0}>
            {printing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-1.5 h-3.5 w-3.5" />}
            Preview / Print ({printableItems.reduce((sum, item) => sum + item.printQty, 0)})
          </Button>
        </div>
      </div>

      {error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Inventory Code</th>
              <th className="px-3 py-2 text-left font-medium">QC Code</th>
              <th className="px-3 py-2 text-left font-medium">Description</th>
              <th className="px-3 py-2 text-left font-medium">Trace</th>
              <th className="px-3 py-2 text-right font-medium">Received</th>
              <th className="px-3 py-2 text-right font-medium">Print Qty</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((item) => (
              <tr key={item.lineId} className="border-t align-top">
                <td className="px-3 py-2">
                  <div className="font-mono text-xs font-semibold">{item.inventoryCode || item.skuCode}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{item.skuCode}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{item.qcCode || '-'}</td>
                <td className="px-3 py-2">
                  <div className="max-w-[360px] text-xs font-medium">{item.description}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{item.status ?? 'LINE'}</div>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  <div>Project: {item.projectCode || '-'}</div>
                  <div>PO: {item.poNumber || '-'}</div>
                  <div>Heat: {item.heatNo || '-'}</div>
                  <div>Mill Cert: {item.millCertNo || '-'}</div>
                  <div>Country: {item.countryOfOrigin || '-'}</div>
                  <div>Document Upload: {item.documentUpload || '-'}</div>
                  <div>Bin: {item.binCode || '-'}</div>
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  <div>{item.qtyReceived}</div>
                  <div className="text-muted-foreground">exp {item.qtyExpected}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={500}
                      className="h-8 w-24 text-right text-xs"
                      value={qtyByLine[item.lineId] ?? 0}
                      onChange={(event) => setQtyByLine((current) => ({
                        ...current,
                        [item.lineId]: Math.max(0, Math.min(500, Number(event.target.value) || 0)),
                      }))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => void handleOpenScan(item)}
                      disabled={scanLoadingLineId === item.lineId}
                    >
                      {scanLoadingLineId === item.lineId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Scan'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && (data?.items ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No inbound lines available for sticker printing.</div>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <ExternalLink className="h-3.5 w-3.5" />
        QR code opens the shared receiving item page with inbound, inventory, QC and document upload details.
      </div>
      <Dialog open={scanPreview.open} onOpenChange={(open) => setScanPreview((current) => ({ ...current, open }))}>
        <DialogContent className="w-[min(92vw,430px)] max-w-[430px] gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="text-base">{scanPreview.title}</DialogTitle>
            <DialogDescription>Phone preview</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center bg-slate-100 p-3">
            {scanPreview.url ? (
              <iframe
                title={scanPreview.title}
                src={scanPreview.url}
                className="h-[min(760px,78vh)] w-[390px] max-w-full rounded-[28px] border bg-white shadow-sm"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
